import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { EyeIcon } from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import { Alert, ColorPicker, Divider, InputWithLabel, Text, UploadImage } from '@reveni-io/reveni-ui'

import { previewSections } from '../constants'
import { imageGroups } from '../hooks/useThemeImages/constants'
import { hiddenGroups, imageGroupsNames, previewableGroups } from './constants'
import { getImagesByGroup } from './helpers'
import * as Styles from './styles'

const BrandAndImages = ({
  images = {},
  changeImage,
  changeLink,
  deleteImage,
  disabled,
  onPreviewSection,
  activePreviewSection,
  trackingPreviewUrl,
  brandColors,
  changeColor,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const groupedImages = getImagesByGroup(images)

  const visibleGroups = Object.entries(groupedImages).filter(([groupName]) => !hiddenGroups.includes(groupName))

  return (
    <Styles.Container>
      <Styles.GroupContainer>
        {visibleGroups.map(([groupName, groupImages], index) => {
          const previewSection = previewableGroups[groupName]
          const isActivePreview = previewSection && activePreviewSection === previewSection

          return (
            <Fragment key={groupName}>
              <Styles.GroupSection>
                {index > 0 && (
                  <Styles.SectionDivider>
                    <Divider />
                  </Styles.SectionDivider>
                )}
                <Styles.GroupSectionContent>
                  <Styles.GroupHeader>
                    <Text type="titleS">{t(imageGroupsNames[groupName])}</Text>
                    {previewSection && (previewSection !== previewSections.trackingPage || trackingPreviewUrl) && (
                      <Styles.PreviewButton active={isActivePreview} onClick={() => onPreviewSection?.(previewSection)}>
                        <EyeIcon size={20} color={isActivePreview ? theme.colors.text.inverse : undefined} />
                      </Styles.PreviewButton>
                    )}
                  </Styles.GroupHeader>
                  <Styles.Group>
                    {Object.entries(groupImages).map(([key, image]) => (
                      <Styles.ImageAndLinkContainer key={key}>
                        <UploadImage
                          label={t(image?.label)}
                          recommendation={t(image?.recommendation)}
                          onChange={file => changeImage(key, file)}
                          defaultImage={image?.preview}
                          maxSize={image?.maxSize}
                          accept={image?.accept}
                          onDelete={() => deleteImage(key)}
                          aspectRatio={image?.aspectRatio}
                          disabled={disabled}
                        />
                        {image?.link && (
                          <InputWithLabel
                            placeholder={t(image?.link.placeholder)}
                            value={image?.link.url}
                            onChange={e => changeLink(key, e.target.value)}
                            helperText={t(image?.link.helperText)}
                            fullWidth
                            startAdornment={<image.link.Icon size={16} />}
                            disabled={disabled}
                          />
                        )}
                      </Styles.ImageAndLinkContainer>
                    ))}
                    {groupName === imageGroups.backgrounds && (
                      <Alert type="info">
                        {t('Cuando se han subido ambas imágenes, priorizamos la vista en móvil')}
                      </Alert>
                    )}
                    {groupName === imageGroups.general &&
                      brandColors &&
                      Object.entries(brandColors.types).map(([typeKey, type]) =>
                        Object.entries(type.colors).map(([colorKey, { label, color, orientation }]) => (
                          <Styles.ColorPickerContainer key={colorKey}>
                            <ColorPicker
                              orientation={orientation}
                              onChange={value =>
                                changeColor({ value, group: 'brandColors', type: typeKey, color: colorKey })
                              }
                              label={t(label)}
                              defaultColor={color}
                              disabled={disabled}
                            />
                          </Styles.ColorPickerContainer>
                        ))
                      )}
                  </Styles.Group>
                </Styles.GroupSectionContent>
              </Styles.GroupSection>
            </Fragment>
          )
        })}
      </Styles.GroupContainer>
    </Styles.Container>
  )
}

export default BrandAndImages
